import type { ReactNode } from 'react'
import { Accordion } from '@base-ui/react/accordion'
import { Link } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { PlusIcon } from 'lucide-react'
import { ContactForm } from './contact-form'
import { FEATURE_FLAGS } from './feature-flags'
import { Reveal } from './reveal'

/**
 * The page's close, in reading order: the questions companies ask before starting on the left (first when stacked), then
 * the contact form on the right, for whatever the answers leave open or a demo. The answers are an accordion, one open at
 * a time.
 */
export function FaqSection() {
  const { t } = useLingui()
  // Written for companies, the audience of the form beside them; the professionals' questions are answered in their own
  // section higher up. An answer is plain text unless it has somewhere to send the reader, as the API one does.
  const faqs: Array<[string, ReactNode]> = [
    [
      t`What is a Psychographic Profile?`,
      t`It is a structured description of how a person thinks, decides and works: competences, values and personality. It is built from our test and expressed as indicators you can compare with a position, a team or the market.`,
    ],
    [
      t`How do we get our people to take the test?`,
      t`All you need is their email to send them the invitation. The test takes about 22 minutes, works on any device, and can be paused and resumed at any time.`,
    ],
    [
      t`What will we see of each professional?`,
      t`Their Psychographic Profile and how it fits the positions, teams and offers you select.`,
    ],
    [
      t`Is it scientifically validated?`,
      t`The model rests on established psychometric research and is continuously recalibrated with our own sample of analyzed profiles.`,
    ],
    [
      t`How do you handle our data?`,
      t`We apply the various international privacy and protection regulations: data is kept secure, information is never sold to third parties, and accounts and results can be deleted at any time.`,
    ],
    [
      t`How does the API work?`,
      <>
        {t`It is a REST API for your server plus an SDK that runs in the browser to carry the test flow. You create positions and professionals, invite them, and read fit levels and reports as JSON.`}{' '}
        {/* The reference itself, for the reader this answer has just told what the API does. */}
        <Link to="/docs" className="text-primary font-medium underline-offset-4 hover:underline">
          {t`See the documentation`}
        </Link>
      </>,
    ],
    // Pricing is behind a flag, and so is the question about it.
    ...(FEATURE_FLAGS.showPricing
      ? ([
          [
            t`What are the limits of each plan?`,
            t`The Companies plan is billed by employee band, from 75 €/month for up to 25 employees, and includes every feature. The Professionals plan is free with no time limit.`,
          ],
        ] as Array<[string, ReactNode]>)
      : []),
  ]

  return (
    <section id="faq" className="scroll-mt-20 pt-20 pb-32 [--muted-foreground:var(--foreground)]">
      <div className="mx-auto grid max-w-6xl gap-14 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">{t`FAQ`}</h2>
          {/* One answer open at a time; the panel's height animates from Base UI's measured `--accordion-panel-height`,
              and closed answers stay `hidden="until-found"`, so find-in-page still opens them. */}
          <Accordion.Root multiple={false} hiddenUntilFound className="mt-6 grid">
            {faqs.map(([question, answer]) => (
              <Accordion.Item key={question} className="border-b">
                <Accordion.Header className="m-0">
                  <Accordion.Trigger className="group flex w-full cursor-pointer items-start gap-4 py-4.5 text-left leading-snug font-semibold outline-none focus-visible:underline">
                    {question}
                    <PlusIcon className="text-primary ml-auto size-5 shrink-0 transition-transform duration-300 ease-out group-data-[panel-open]:rotate-45" />
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Panel className="h-(--accordion-panel-height) overflow-hidden transition-[height] duration-300 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 motion-reduce:transition-none">
                  <p className="pr-10 pb-5 text-sm leading-relaxed">{answer}</p>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </Reveal>

        <Reveal delay={140}>
          <div id="contact" className="scroll-mt-24">
            <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">{t`Contact us`}</h2>
            <p className="text-foreground mt-3 mb-8 text-lg leading-relaxed text-pretty">
              {t`Have questions, or want to see the platform in action? Write to us or book a demo to discover how to take control of talent in your company.`}
            </p>
            <ContactForm compact />
          </div>
        </Reveal>
      </div>
    </section>
  )
}
